import { c as _c } from "react/compiler-runtime";
import { useKeyedState } from "@/hooks/useKeyedState";
import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, useDeferredValue, startTransition } from "react";
import { useLocalStorage, useDebounceValue } from "usehooks-ts";
import { useDashboardSearchParams as useSearchParams } from "@/hooks/useDashboardNavigation";
import { useQueryClient } from "react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronRight, Search, Sparkles, X, Check, Copy, ExternalLink, Briefcase, ArrowDownUp } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItemRight, DropdownMenuSeparator } from "@repo/blacksmith-ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useAuth } from "@/components/AuthProvider";
import { useApi } from "@/hooks/useApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/blacksmith-ui/select";
import { cn } from "@repo/blacksmith-ui/cn";
import { formatTestDuration } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActors } from "@/hooks/useActors";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { TestFailureDrawer } from "@/components/TestFailureDrawer";
import { AutoParsedTestDetailView } from "@/components/AutoParsedTestDetailView";
import { JunitTestDetailView } from "@/components/JunitTestDetailView";
import { FixAiFloatingButton } from "@/components/FixAiFloatingButton";
import { HoverTickerText } from "@/components/ui/hover-ticker-text";
import { Input } from "@repo/blacksmith-ui/input";
import { FilterBadge } from "@/components/ui/filter-badge";
import { TestListSkeleton } from "@/components/TestListSkeleton";
import type { TestHistoryRun } from "@/hooks/useTestHistory";
import type { BranchFilterType, StatusFilterType } from "@/hooks/useTestFilters";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
export interface TestCheck {
  id: string;
  test_name: string;
  test_suite: string | null;
  test_status: "pass" | "fail" | "skip" | "error";
  test_type?: "junit" | "auto";
  duration_seconds?: number;
  logs?: string;
  step_record_id?: string;
  step_order?: number;
  log_line_start?: number;
  log_line_end?: number;
  branch?: string | null;
  // Workflow-level fields
  job_id?: string;
  job_name?: string;
}
interface TestSuite {
  name: string;
  tests: TestCheck[];
  status: "pass" | "fail" | "skip";
  passCount: number;
  failCount: number;
  skipCount: number;
}
type FlatRowType = {
  type: "suite";
  suite: TestSuite;
} | {
  type: "test";
  test: TestCheck;
  suiteName: string;
};
type SortField = "match-logs" | "title" | "duration";
type TitleSortOrder = "alphabetical" | "reverse";
type DurationSortOrder = "shortest" | "longest";

// Row height constants for virtualization (must match CSS)
const ROW_HEIGHT_SUITE = 42;
const ROW_HEIGHT_TEST = 42;

// Stable default so effects/memos depending on jobFilters don't re-run every
// render when the prop is omitted (job-level pages don't pass it)
const EMPTY_JOB_FILTERS: string[] = [];
interface TestListProps {
  checks: TestCheck[];
  isLoading?: boolean;
  onTestSelect?: (test: TestCheck | null) => void;
  selectedTestId?: string | null;
  isActive?: boolean;
  repository: string;
  org: string;
  runId: string;
  jobId?: string; // Optional for workflow-level test views
  branch?: string | null;
  prNumber?: number | null;
  onNavigationStart?: (targetRunId: string, targetJobId: string) => void;
  logs?: import("@/hooks/useJobDetails").JobLogsResponse;
  steps?: import("@/hooks/useJobDetails").JobDetailsResponse["job"]["steps"];
  isLogsLoading?: boolean;
  // Filter state - passed from parent to avoid duplicate hook instances
  statusFilters: StatusFilterType[];
  timeRangeHours: number;
  branchFilter: BranchFilterType;
  zoomStart: number | null;
  zoomEnd: number | null;
  onStatusFiltersChange: (filters: StatusFilterType[]) => void;
  onTimeRangeChange: (hours: number) => void;
  onBranchFilterChange: (branch: BranchFilterType) => void;
  onZoomChange: (start: number | null, end: number | null) => void;
  isPendingDefaultFilter: boolean;
  // Job filter (workflow-level only)
  jobFilters?: string[];
  onJobFiltersChange?: (jobs: string[]) => void;
}

// Calculate suite status based on children
function calculateSuiteStatus(tests: TestCheck[]): "pass" | "fail" | "skip" {
  const hasFailures = tests.some(t => t.test_status === "fail" || t.test_status === "error");
  if (hasFailures) return "fail";
  const allSkipped = tests.every(t => t.test_status === "skip");
  if (allSkipped) return "skip";
  return "pass";
}
const COLORS = {
  error: STATUS_COLORS.failure,
  success: STATUS_COLORS.success,
  neutral: "#A3A3A3",
  // Neutral - 400 (gray for skipped)
  textDefault: "#131313",
  // Text/Default/Default
  textSecondary: "#525252",
  // Text/Default/Secondary
  bgSecondary: "#F3F3F3" // Background/Default/Secondary
};

// Dark mode color overrides from Figma
const COLORS_DARK = {
  textDefault: "#FFFFFF",
  // White text in dark mode
  textSecondary: "#A3A3A3",
  // Neutral-400 for secondary text
  bgSecondary: "#262626",
  // Neutral-800 for search bar bg
  bgCard: "#171717" // Neutral-900 for card background
};
function StatusIcon(t0) {
  const $ = _c(20);
  const {
    status,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? 12 : t1;
  if (status === "fail" || status === "error") {
    let t2;
    if ($[0] !== className) {
      t2 = cn("flex-shrink-0", className);
      $[0] = className;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <circle cx="8" cy="8" r="7" stroke={COLORS.error} strokeWidth={1.25} fill="none" />;
      t4 = <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke={COLORS.error} strokeWidth={1.25} strokeLinecap="round" />;
      $[2] = t3;
      $[3] = t4;
    } else {
      t3 = $[2];
      t4 = $[3];
    }
    let t5;
    if ($[4] !== size || $[5] !== t2) {
      t5 = <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={t2}>{t3}{t4}</svg>;
      $[4] = size;
      $[5] = t2;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (status === "pass") {
    let t2;
    if ($[7] !== className) {
      t2 = cn("flex-shrink-0", className);
      $[7] = className;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <circle cx="8" cy="8" r="7" stroke={COLORS.success} strokeWidth={1.25} fill="none" />;
      t4 = <path d="M5 8L7 10.5L11 5.5" stroke={COLORS.success} strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" />;
      $[9] = t3;
      $[10] = t4;
    } else {
      t3 = $[9];
      t4 = $[10];
    }
    let t5;
    if ($[11] !== size || $[12] !== t2) {
      t5 = <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={t2}>{t3}{t4}</svg>;
      $[11] = size;
      $[12] = t2;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t2;
  if ($[14] !== className) {
    t2 = cn("flex-shrink-0", className);
    $[14] = className;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <circle cx="8" cy="8" r="7" stroke={COLORS.neutral} strokeWidth={1.25} fill="none" />;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== size || $[18] !== t2) {
    t4 = <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={t2}>{t3}</svg>;
    $[17] = size;
    $[18] = t2;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}
function getStatusLabel(filter: StatusFilterType): string {
  switch (filter) {
    case "failed":
      return "Failed";
    case "pass":
      return "Passed";
    case "skipped":
      return "Skipped";
  }
}
function getStatusColor(filter: StatusFilterType): string {
  switch (filter) {
    case "failed":
      return COLORS.error;
    case "pass":
      return COLORS.success;
    case "skipped":
      return COLORS.neutral;
  }
}

// Clicking the checkbox toggles the filter and keeps the menu open; clicking
// anywhere else on the row toggles and closes it.
function StatusFilterMenuItem(t0) {
  const $ = _c(37);
  const {
    filter,
    checked,
    statusFilters,
    onStatusFiltersChange,
    onClose,
    count,
    disabled
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== filter || $[2] !== onStatusFiltersChange || $[3] !== statusFilters) {
    t1 = e => {
      e.stopPropagation();
      e.preventDefault();
      startTransition(() => {
        if (checked) {
          onStatusFiltersChange(statusFilters.filter(f => f !== filter));
        } else {
          onStatusFiltersChange([...statusFilters, filter]);
        }
      });
    };
    $[0] = checked;
    $[1] = filter;
    $[2] = onStatusFiltersChange;
    $[3] = statusFilters;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleCheckboxClick = t1;
  let t2;
  if ($[5] !== filter || $[6] !== onClose || $[7] !== onStatusFiltersChange || $[8] !== statusFilters) {
    t2 = e_0 => {
      const target = e_0.target as HTMLElement;
      if (target.closest("[data-checkbox-area]")) {
        return;
      }
      startTransition(() => {
        if (statusFilters.includes(filter)) {
          onStatusFiltersChange(statusFilters.filter(f_0 => f_0 !== filter));
        } else {
          onStatusFiltersChange([...statusFilters, filter]);
        }
      });
      onClose();
    };
    $[5] = filter;
    $[6] = onClose;
    $[7] = onStatusFiltersChange;
    $[8] = statusFilters;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleItemClick = t2;
  const t3 = `flex h-4 w-4 items-center justify-center rounded-sm border transition-colors ${checked ? "bg-neutral-900 border-neutral-900 dark:bg-neutral-100 dark:border-neutral-100" : "border-[var(--color-border)] dark:border-[var(--color-border)] bg-transparent"}`;
  let t4;
  if ($[10] !== checked) {
    t4 = checked && <Check className="h-3 w-3 text-white dark:text-black" strokeWidth={3} />;
    $[10] = checked;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== handleCheckboxClick || $[16] !== t5) {
    t6 = <span data-checkbox-area={true} className="absolute left-2 flex items-center justify-center cursor-pointer z-10" onClick={handleCheckboxClick}>{t5}</span>;
    $[15] = handleCheckboxClick;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== filter) {
    t7 = getStatusColor(filter);
    $[18] = filter;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <span className="w-2 h-2 rounded-full" style={{
      backgroundColor: t7
    }} />;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== filter) {
    t9 = getStatusLabel(filter);
    $[22] = filter;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <span className="text-neutral-900 dark:text-white">{t9}</span>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== count) {
    t11 = count !== undefined && <span className="text-neutral-400 dark:text-neutral-500 text-sm ml-auto">{count}</span>;
    $[26] = count;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t8) {
    t12 = <div className="flex items-center gap-2 flex-1">{t8}{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t8;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== disabled || $[33] !== handleItemClick || $[34] !== t12 || $[35] !== t6) {
    t13 = <DropdownMenuItem disabled={disabled} onClick={handleItemClick} onSelect={_temp} className="relative pl-9 transition-[background-color] duration-0 hover:bg-neutral-100 dark:hover:bg-[#444545]">{t6}{t12}</DropdownMenuItem>;
    $[32] = disabled;
    $[33] = handleItemClick;
    $[34] = t12;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp(e_1) {
  e_1.preventDefault();
}
function StatusFilterDropdown(t0) {
  const $ = _c(133);
  const {
    statusFilters,
    onStatusFiltersChange,
    trigger,
    openSubmenuDirectly: t1,
    isClearingRef,
    counts,
    controlledOpen,
    onControlledOpenChange,
    jobFilters,
    onJobFiltersChange,
    availableJobs
  } = t0;
  const openSubmenuDirectly = t1 === undefined ? false : t1;
  const [internalOpen, setInternalOpen] = useState(false);
  const hasJobFilters = availableJobs && availableJobs.length > 0 && onJobFiltersChange;
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  let t2;
  if ($[0] !== onControlledOpenChange) {
    t2 = newOpen => {
      if (onControlledOpenChange) {
        onControlledOpenChange(newOpen);
      }
      setInternalOpen(newOpen);
    };
    $[0] = onControlledOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const setOpen = t2;
  const [submenuOpen, setSubmenuOpen] = useKeyedState(open, open && !!openSubmenuDirectly);
  let t3;
  if ($[2] !== statusFilters) {
    t3 = statusFilters.includes("failed");
    $[2] = statusFilters;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== setOpen) {
    t4 = () => setOpen(false);
    $[4] = setOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = counts?.failed;
  let t6;
  if ($[6] !== counts?.failed || $[7] !== statusFilters) {
    t6 = !statusFilters.includes("failed") && (counts?.failed ?? 0) === 0;
    $[6] = counts?.failed;
    $[7] = statusFilters;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onStatusFiltersChange || $[10] !== statusFilters || $[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <StatusFilterMenuItem filter="failed" checked={t3} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t4} count={t5} disabled={t6} />;
    $[9] = onStatusFiltersChange;
    $[10] = statusFilters;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== statusFilters) {
    t8 = statusFilters.includes("pass");
    $[16] = statusFilters;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== setOpen) {
    t9 = () => setOpen(false);
    $[18] = setOpen;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = counts?.pass;
  let t11;
  if ($[20] !== counts?.pass || $[21] !== statusFilters) {
    t11 = !statusFilters.includes("pass") && (counts?.pass ?? 0) === 0;
    $[20] = counts?.pass;
    $[21] = statusFilters;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== onStatusFiltersChange || $[24] !== statusFilters || $[25] !== t10 || $[26] !== t11 || $[27] !== t8 || $[28] !== t9) {
    t12 = <StatusFilterMenuItem filter="pass" checked={t8} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t9} count={t10} disabled={t11} />;
    $[23] = onStatusFiltersChange;
    $[24] = statusFilters;
    $[25] = t10;
    $[26] = t11;
    $[27] = t8;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== statusFilters) {
    t13 = statusFilters.includes("skipped");
    $[30] = statusFilters;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== setOpen) {
    t14 = () => setOpen(false);
    $[32] = setOpen;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = counts?.skipped;
  let t16;
  if ($[34] !== counts?.skipped || $[35] !== statusFilters) {
    t16 = !statusFilters.includes("skipped") && (counts?.skipped ?? 0) === 0;
    $[34] = counts?.skipped;
    $[35] = statusFilters;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== onStatusFiltersChange || $[38] !== statusFilters || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = <StatusFilterMenuItem filter="skipped" checked={t13} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t14} count={t15} disabled={t16} />;
    $[37] = onStatusFiltersChange;
    $[38] = statusFilters;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t12 || $[45] !== t17 || $[46] !== t7) {
    t18 = <DropdownMenuSubContent>{t7}{t12}{t17}</DropdownMenuSubContent>;
    $[44] = t12;
    $[45] = t17;
    $[46] = t7;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const submenuContent = t18;
  let t19;
  if ($[48] !== availableJobs || $[49] !== hasJobFilters || $[50] !== jobFilters || $[51] !== onJobFiltersChange || $[52] !== setOpen) {
    t19 = hasJobFilters ? <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">{availableJobs.map(job => <JobFilterMenuItem key={job.name} jobName={job.name} checked={jobFilters?.includes(job.name) ?? false} jobFilters={jobFilters ?? []} onJobFiltersChange={onJobFiltersChange} onClose={() => setOpen(false)} count={job.count} />)}</DropdownMenuSubContent> : null;
    $[48] = availableJobs;
    $[49] = hasJobFilters;
    $[50] = jobFilters;
    $[51] = onJobFiltersChange;
    $[52] = setOpen;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const jobsSubmenuContent = t19;
  if (openSubmenuDirectly) {
    let t20;
    if ($[54] !== isClearingRef || $[55] !== setOpen) {
      t20 = newOpen_0 => {
        if (newOpen_0 && isClearingRef?.current) {
          return;
        }
        setOpen(newOpen_0);
      };
      $[54] = isClearingRef;
      $[55] = setOpen;
      $[56] = t20;
    } else {
      t20 = $[56];
    }
    let t21;
    if ($[57] !== trigger) {
      t21 = <DropdownMenuTrigger asChild={true}>{trigger}</DropdownMenuTrigger>;
      $[57] = trigger;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    let t22;
    if ($[59] !== statusFilters) {
      t22 = statusFilters.includes("failed");
      $[59] = statusFilters;
      $[60] = t22;
    } else {
      t22 = $[60];
    }
    let t23;
    if ($[61] !== setOpen) {
      t23 = () => setOpen(false);
      $[61] = setOpen;
      $[62] = t23;
    } else {
      t23 = $[62];
    }
    const t24 = counts?.failed;
    let t25;
    if ($[63] !== counts?.failed || $[64] !== statusFilters) {
      t25 = !statusFilters.includes("failed") && (counts?.failed ?? 0) === 0;
      $[63] = counts?.failed;
      $[64] = statusFilters;
      $[65] = t25;
    } else {
      t25 = $[65];
    }
    let t26;
    if ($[66] !== onStatusFiltersChange || $[67] !== statusFilters || $[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== t25) {
      t26 = <StatusFilterMenuItem filter="failed" checked={t22} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t23} count={t24} disabled={t25} />;
      $[66] = onStatusFiltersChange;
      $[67] = statusFilters;
      $[68] = t22;
      $[69] = t23;
      $[70] = t24;
      $[71] = t25;
      $[72] = t26;
    } else {
      t26 = $[72];
    }
    let t27;
    if ($[73] !== statusFilters) {
      t27 = statusFilters.includes("pass");
      $[73] = statusFilters;
      $[74] = t27;
    } else {
      t27 = $[74];
    }
    let t28;
    if ($[75] !== setOpen) {
      t28 = () => setOpen(false);
      $[75] = setOpen;
      $[76] = t28;
    } else {
      t28 = $[76];
    }
    const t29 = counts?.pass;
    let t30;
    if ($[77] !== counts?.pass || $[78] !== statusFilters) {
      t30 = !statusFilters.includes("pass") && (counts?.pass ?? 0) === 0;
      $[77] = counts?.pass;
      $[78] = statusFilters;
      $[79] = t30;
    } else {
      t30 = $[79];
    }
    let t31;
    if ($[80] !== onStatusFiltersChange || $[81] !== statusFilters || $[82] !== t27 || $[83] !== t28 || $[84] !== t29 || $[85] !== t30) {
      t31 = <StatusFilterMenuItem filter="pass" checked={t27} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t28} count={t29} disabled={t30} />;
      $[80] = onStatusFiltersChange;
      $[81] = statusFilters;
      $[82] = t27;
      $[83] = t28;
      $[84] = t29;
      $[85] = t30;
      $[86] = t31;
    } else {
      t31 = $[86];
    }
    let t32;
    if ($[87] !== statusFilters) {
      t32 = statusFilters.includes("skipped");
      $[87] = statusFilters;
      $[88] = t32;
    } else {
      t32 = $[88];
    }
    let t33;
    if ($[89] !== setOpen) {
      t33 = () => setOpen(false);
      $[89] = setOpen;
      $[90] = t33;
    } else {
      t33 = $[90];
    }
    const t34 = counts?.skipped;
    let t35;
    if ($[91] !== counts?.skipped || $[92] !== statusFilters) {
      t35 = !statusFilters.includes("skipped") && (counts?.skipped ?? 0) === 0;
      $[91] = counts?.skipped;
      $[92] = statusFilters;
      $[93] = t35;
    } else {
      t35 = $[93];
    }
    let t36;
    if ($[94] !== onStatusFiltersChange || $[95] !== statusFilters || $[96] !== t32 || $[97] !== t33 || $[98] !== t34 || $[99] !== t35) {
      t36 = <StatusFilterMenuItem filter="skipped" checked={t32} statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} onClose={t33} count={t34} disabled={t35} />;
      $[94] = onStatusFiltersChange;
      $[95] = statusFilters;
      $[96] = t32;
      $[97] = t33;
      $[98] = t34;
      $[99] = t35;
      $[100] = t36;
    } else {
      t36 = $[100];
    }
    let t37;
    if ($[101] !== t26 || $[102] !== t31 || $[103] !== t36) {
      t37 = <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="w-auto min-w-[8rem]">{t26}{t31}{t36}</DropdownMenuContent>;
      $[101] = t26;
      $[102] = t31;
      $[103] = t36;
      $[104] = t37;
    } else {
      t37 = $[104];
    }
    let t38;
    if ($[105] !== open || $[106] !== t20 || $[107] !== t21 || $[108] !== t37) {
      t38 = <DropdownMenu open={open} onOpenChange={t20}>{t21}{t37}</DropdownMenu>;
      $[105] = open;
      $[106] = t20;
      $[107] = t21;
      $[108] = t37;
      $[109] = t38;
    } else {
      t38 = $[109];
    }
    return t38;
  }
  let t20;
  if ($[110] !== trigger) {
    t20 = <DropdownMenuTrigger asChild={true}>{trigger}</DropdownMenuTrigger>;
    $[110] = trigger;
    $[111] = t20;
  } else {
    t20 = $[111];
  }
  let t21;
  if ($[112] !== setSubmenuOpen) {
    t21 = () => setSubmenuOpen(true);
    $[112] = setSubmenuOpen;
    $[113] = t21;
  } else {
    t21 = $[113];
  }
  let t22;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full border border-blue-600 dark:border-[var(--color-border)] flex items-center justify-center flex-shrink-0"><svg className="w-2 h-2 text-blue-600 dark:text-neutral-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div><span>Status</span></div>;
    $[114] = t22;
  } else {
    t22 = $[114];
  }
  let t23;
  if ($[115] !== t21) {
    t23 = <DropdownMenuSubTrigger onPointerEnter={t21}>{t22}</DropdownMenuSubTrigger>;
    $[115] = t21;
    $[116] = t23;
  } else {
    t23 = $[116];
  }
  let t24;
  if ($[117] !== setSubmenuOpen || $[118] !== submenuContent || $[119] !== submenuOpen || $[120] !== t23) {
    t24 = <DropdownMenuSub open={submenuOpen} onOpenChange={setSubmenuOpen}>{t23}{submenuContent}</DropdownMenuSub>;
    $[117] = setSubmenuOpen;
    $[118] = submenuContent;
    $[119] = submenuOpen;
    $[120] = t23;
    $[121] = t24;
  } else {
    t24 = $[121];
  }
  let t25;
  if ($[122] !== hasJobFilters || $[123] !== jobsSubmenuContent) {
    t25 = hasJobFilters && <DropdownMenuSub><DropdownMenuSubTrigger><div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-blue-600 dark:text-neutral-200" /><span>Jobs</span></div></DropdownMenuSubTrigger>{jobsSubmenuContent}</DropdownMenuSub>;
    $[122] = hasJobFilters;
    $[123] = jobsSubmenuContent;
    $[124] = t25;
  } else {
    t25 = $[124];
  }
  let t26;
  if ($[125] !== t24 || $[126] !== t25) {
    t26 = <DropdownMenuContent align="start" className="w-36">{t24}{t25}</DropdownMenuContent>;
    $[125] = t24;
    $[126] = t25;
    $[127] = t26;
  } else {
    t26 = $[127];
  }
  let t27;
  if ($[128] !== open || $[129] !== setOpen || $[130] !== t20 || $[131] !== t26) {
    t27 = <DropdownMenu open={open} onOpenChange={setOpen}>{t20}{t26}</DropdownMenu>;
    $[128] = open;
    $[129] = setOpen;
    $[130] = t20;
    $[131] = t26;
    $[132] = t27;
  } else {
    t27 = $[132];
  }
  return t27;
}
function JobFilterMenuItem(t0) {
  const $ = _c(30);
  const {
    jobName,
    checked,
    jobFilters,
    onJobFiltersChange,
    onClose,
    count
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== jobFilters || $[2] !== jobName || $[3] !== onJobFiltersChange) {
    t1 = e => {
      e.stopPropagation();
      e.preventDefault();
      if (checked) {
        onJobFiltersChange(jobFilters.filter(j => j !== jobName));
      } else {
        onJobFiltersChange([...jobFilters, jobName]);
      }
    };
    $[0] = checked;
    $[1] = jobFilters;
    $[2] = jobName;
    $[3] = onJobFiltersChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleCheckboxClick = t1;
  let t2;
  if ($[5] !== checked || $[6] !== jobFilters || $[7] !== jobName || $[8] !== onClose || $[9] !== onJobFiltersChange) {
    t2 = e_0 => {
      const target = e_0.target as HTMLElement;
      if (target.closest("[data-checkbox-area]")) {
        return;
      }
      e_0.preventDefault();
      requestAnimationFrame(() => {
        if (checked) {
          onJobFiltersChange(jobFilters.filter(j_0 => j_0 !== jobName));
        } else {
          onJobFiltersChange([...jobFilters, jobName]);
        }
      });
      onClose();
    };
    $[5] = checked;
    $[6] = jobFilters;
    $[7] = jobName;
    $[8] = onClose;
    $[9] = onJobFiltersChange;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleItemClick = t2;
  const t3 = `flex h-4 w-4 items-center justify-center rounded-sm border transition-colors ${checked ? "bg-neutral-900 border-neutral-900 dark:bg-neutral-100 dark:border-neutral-100" : "border-[var(--color-border)] dark:border-[var(--color-border)] bg-transparent"}`;
  let t4;
  if ($[11] !== checked) {
    t4 = checked && <Check className="h-3 w-3 text-white dark:text-black" strokeWidth={3} />;
    $[11] = checked;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== handleCheckboxClick || $[17] !== t5) {
    t6 = <span data-checkbox-area={true} className="absolute left-2 flex items-center justify-center cursor-pointer z-10" onClick={handleCheckboxClick}>{t5}</span>;
    $[16] = handleCheckboxClick;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== jobName) {
    t7 = <span className="text-neutral-900 dark:text-white truncate">{jobName}</span>;
    $[19] = jobName;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== count) {
    t8 = count !== undefined && <span className="text-neutral-400 dark:text-neutral-500 text-sm ml-auto flex-shrink-0">{count}</span>;
    $[21] = count;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <div className="flex items-center gap-2 flex-1 min-w-0">{t7}{t8}</div>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== handleItemClick || $[27] !== t6 || $[28] !== t9) {
    t10 = <DropdownMenuItem onClick={handleItemClick} onSelect={_temp2} className="relative pl-9 transition-[background-color] duration-0 hover:bg-neutral-100 dark:hover:bg-[#444545]">{t6}{t9}</DropdownMenuItem>;
    $[26] = handleItemClick;
    $[27] = t6;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp2(e_1) {
  e_1.preventDefault();
}
function JobFilterDropdown(t0) {
  const $ = _c(15);
  const {
    jobFilters,
    onJobFiltersChange,
    availableJobs,
    trigger
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== trigger) {
    t1 = <DropdownMenuTrigger asChild={true}>{trigger}</DropdownMenuTrigger>;
    $[0] = trigger;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== availableJobs || $[3] !== jobFilters || $[4] !== onJobFiltersChange) {
    let t3;
    if ($[6] !== jobFilters || $[7] !== onJobFiltersChange) {
      t3 = job => <JobFilterMenuItem key={job.name} jobName={job.name} checked={jobFilters.includes(job.name)} jobFilters={jobFilters} onJobFiltersChange={onJobFiltersChange} onClose={() => setOpen(false)} count={job.count} />;
      $[6] = jobFilters;
      $[7] = onJobFiltersChange;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = availableJobs.map(t3);
    $[2] = availableJobs;
    $[3] = jobFilters;
    $[4] = onJobFiltersChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="w-auto min-w-[12rem] max-w-[20rem] max-h-[300px] overflow-y-auto">{t2}</DropdownMenuContent>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== open || $[12] !== t1 || $[13] !== t3) {
    t4 = <DropdownMenu open={open} onOpenChange={setOpen}>{t1}{t3}</DropdownMenu>;
    $[11] = open;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function SortDropdown(t0) {
  const $ = _c(27);
  const {
    sortField,
    titleSortOrder,
    durationSortOrder,
    onSortFieldChange,
    onTitleSortOrderChange,
    onDurationSortOrderChange,
    trigger,
    controlledOpen,
    onControlledOpenChange
  } = t0;
  let t1;
  if ($[0] !== trigger) {
    t1 = <DropdownMenuTrigger asChild={true}>{trigger}</DropdownMenuTrigger>;
    $[0] = trigger;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSortFieldChange) {
    t2 = value => onSortFieldChange(value as SortField);
    $[2] = onSortFieldChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DropdownMenuRadioItemRight value="match-logs" onSelect={_temp3}>Default</DropdownMenuRadioItemRight>;
    t4 = <DropdownMenuRadioItemRight value="title" onSelect={_temp4}>Title</DropdownMenuRadioItemRight>;
    t5 = <DropdownMenuRadioItemRight value="duration" onSelect={_temp5}>Duration</DropdownMenuRadioItemRight>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== sortField || $[8] !== t2) {
    t6 = <DropdownMenuRadioGroup value={sortField} onValueChange={t2}>{t3}{t4}{t5}</DropdownMenuRadioGroup>;
    $[7] = sortField;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onTitleSortOrderChange || $[11] !== sortField || $[12] !== titleSortOrder) {
    t7 = sortField === "title" && <><DropdownMenuSeparator /><DropdownMenuRadioGroup value={titleSortOrder} onValueChange={value_0 => onTitleSortOrderChange(value_0 as TitleSortOrder)}><DropdownMenuRadioItemRight value="alphabetical" onSelect={_temp6}>Alphabetical</DropdownMenuRadioItemRight><DropdownMenuRadioItemRight value="reverse" onSelect={_temp7}>Reverse alphabetical</DropdownMenuRadioItemRight></DropdownMenuRadioGroup></>;
    $[10] = onTitleSortOrderChange;
    $[11] = sortField;
    $[12] = titleSortOrder;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== durationSortOrder || $[15] !== onDurationSortOrderChange || $[16] !== sortField) {
    t8 = sortField === "duration" && <><DropdownMenuSeparator /><DropdownMenuRadioGroup value={durationSortOrder} onValueChange={value_1 => onDurationSortOrderChange(value_1 as DurationSortOrder)}><DropdownMenuRadioItemRight value="longest" onSelect={_temp8}>Longest to shortest</DropdownMenuRadioItemRight><DropdownMenuRadioItemRight value="shortest" onSelect={_temp9}>Shortest to longest</DropdownMenuRadioItemRight></DropdownMenuRadioGroup></>;
    $[14] = durationSortOrder;
    $[15] = onDurationSortOrderChange;
    $[16] = sortField;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <DropdownMenuContent align="start" className="w-52">{t6}{t7}{t8}</DropdownMenuContent>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== controlledOpen || $[23] !== onControlledOpenChange || $[24] !== t1 || $[25] !== t9) {
    t10 = <DropdownMenu open={controlledOpen} onOpenChange={onControlledOpenChange}>{t1}{t9}</DropdownMenu>;
    $[22] = controlledOpen;
    $[23] = onControlledOpenChange;
    $[24] = t1;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}

// Drag detection threshold (horizontal pixels) for text selection vs click
function _temp9(e_5) {
  return e_5.preventDefault();
}
function _temp8(e_4) {
  return e_4.preventDefault();
}
function _temp7(e_3) {
  return e_3.preventDefault();
}
function _temp6(e_2) {
  return e_2.preventDefault();
}
function _temp5(e_1) {
  return e_1.preventDefault();
}
function _temp4(e_0) {
  return e_0.preventDefault();
}
function _temp3(e) {
  return e.preventDefault();
}
const TEXT_DRAG_THRESHOLD_PX = 50;

// Helper to calculate next navigation index with wrap-around
function calculateNextIndex(currentIndex: number, rowsLength: number, isMovingDown: boolean): number {
  if (currentIndex === -1) {
    return isMovingDown ? 0 : rowsLength - 1;
  }
  if (isMovingDown) {
    return (currentIndex + 1) % rowsLength;
  }
  return currentIndex === 0 ? rowsLength - 1 : currentIndex - 1;
}

// Hook to handle drag-to-select text behavior
// Returns handlers and a function to check if click should be suppressed
function useDragToSelect() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      startX: null,
      isDragging: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dragStateRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      dragStateRef.current = {
        startX: e.clientX,
        isDragging: false
      };
      const handlePointerMove = moveEvent => {
        if (dragStateRef.current.startX === null) {
          return;
        }
        const deltaX = Math.abs(moveEvent.clientX - dragStateRef.current.startX);
        if (deltaX > TEXT_DRAG_THRESHOLD_PX) {
          dragStateRef.current.isDragging = true;
        }
      };
      const handlePointerUp = () => {
        setTimeout(() => {
          dragStateRef.current = {
            startX: null,
            isDragging: false
          };
        }, 0);
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
      };
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handlePointerDown = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => dragStateRef.current.isDragging;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const shouldSuppressClick = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      handlePointerDown,
      shouldSuppressClick
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// Individual test row - memoized to only re-render when this specific test's selection changes
const TestRow = React.memo(function TestRow(t0) {
  const $ = _c(35);
  const {
    test,
    isSelected,
    isHovered,
    showDuration,
    onTestSelect,
    onHover
  } = t0;
  const {
    handlePointerDown,
    shouldSuppressClick
  } = useDragToSelect();
  let t1;
  if ($[0] !== onTestSelect || $[1] !== shouldSuppressClick || $[2] !== test) {
    t1 = e => {
      if (shouldSuppressClick()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onTestSelect?.(test);
    };
    $[0] = onTestSelect;
    $[1] = shouldSuppressClick;
    $[2] = test;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleClick = t1;
  const rowBg = isSelected ? "bg-[#F0F0F0] dark:bg-[#2A2B2D]" : isHovered ? "bg-neutral-950/[0.02] dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600" : "hover:bg-neutral-950/[0.03] dark:hover:bg-white/[0.04]";
  const rowPadding = isSelected ? "pr-2" : "pr-1.5";
  let t2;
  if ($[4] !== onHover || $[5] !== test) {
    t2 = () => onHover?.(test);
    $[4] = onHover;
    $[5] = test;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== onHover) {
    t3 = () => onHover?.(null);
    $[7] = onHover;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = test.id;
  const t5 = isSelected ? "true" : undefined;
  let t6;
  if ($[9] !== rowBg || $[10] !== rowPadding) {
    t6 = cn("w-full flex items-center gap-0 h-[42px] text-left outline-none focus:outline-none transition-colors duration-0 cursor-pointer rounded-md py-1.5", rowBg, rowPadding, "pl-4");
    $[9] = rowBg;
    $[10] = rowPadding;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="w-5 h-0 flex-shrink-0" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== test.test_status) {
    t8 = <StatusIcon status={test.test_status} size={12} />;
    $[13] = test.test_status;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== test.test_name) {
    t9 = <HoverTickerText text={test.test_name} className="type-body-s-regular text-neutral-900 dark:text-white flex-1 min-w-0" />;
    $[15] = test.test_name;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="flex items-center gap-1.5 min-w-0 flex-1">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== showDuration || $[21] !== test.duration_seconds) {
    t11 = showDuration && test.duration_seconds != null && <span className="text-[12px] text-neutral-400 dark:text-neutral-500 tabular-nums flex-shrink-0 mr-2">{formatTestDuration(test.duration_seconds)}</span>;
    $[20] = showDuration;
    $[21] = test.duration_seconds;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== handleClick || $[24] !== t10 || $[25] !== t11 || $[26] !== t5 || $[27] !== t6 || $[28] !== test.id) {
    t12 = <button data-test-id={t4} onClick={handleClick} aria-current={t5} className={t6}>{t7}{t10}{t11}</button>;
    $[23] = handleClick;
    $[24] = t10;
    $[25] = t11;
    $[26] = t5;
    $[27] = t6;
    $[28] = test.id;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== handlePointerDown || $[31] !== t12 || $[32] !== t2 || $[33] !== t3) {
    t13 = <div className="group relative w-full select-text" onMouseEnter={t2} onMouseLeave={t3} onPointerDown={handlePointerDown}>{t12}</div>;
    $[30] = handlePointerDown;
    $[31] = t12;
    $[32] = t2;
    $[33] = t3;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
},
// Custom comparison: only re-render if selection, hover, or test data changed
// Callbacks are stable (via ref wrappers) so we don't need to compare them
(prevProps, nextProps) => {
  return prevProps.isSelected === nextProps.isSelected && prevProps.isHovered === nextProps.isHovered && prevProps.showDuration === nextProps.showDuration && prevProps.test.id === nextProps.test.id && prevProps.test.test_status === nextProps.test.test_status && prevProps.test.test_name === nextProps.test.test_name && prevProps.test.duration_seconds === nextProps.test.duration_seconds;
});

// Suite row component with drag-to-select support
const SuiteRow = React.memo(function SuiteRow(t0) {
  const $ = _c(24);
  const {
    suite,
    isExpanded,
    isHovered,
    showDuration,
    onToggle
  } = t0;
  const {
    handlePointerDown,
    shouldSuppressClick
  } = useDragToSelect();
  let t1;
  if ($[0] !== onToggle || $[1] !== shouldSuppressClick) {
    t1 = () => {
      if (shouldSuppressClick()) {
        return;
      }
      onToggle();
    };
    $[0] = onToggle;
    $[1] = shouldSuppressClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClick = t1;
  const rowBg = isHovered ? "bg-neutral-950/[0.02] dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600" : "hover:bg-neutral-950/[0.03] dark:hover:bg-white/[0.04]";
  const totalDuration = suite.tests.reduce(_temp10, 0);
  let t2;
  if ($[3] !== rowBg) {
    t2 = cn("group w-full flex items-center gap-0.5 h-[42px] pl-1 pr-1.5 py-1.5 text-left rounded-md transition-colors duration-0 cursor-pointer", rowBg);
    $[3] = rowBg;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isExpanded) {
    t3 = isExpanded ? <ChevronDown className="w-[14px] h-[14px] flex-shrink-0 text-[#525252] dark:text-[#A3A3A3]" /> : <ChevronRight className="w-[14px] h-[14px] flex-shrink-0 text-[#525252] dark:text-[#A3A3A3]" />;
    $[5] = isExpanded;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== suite.status) {
    t4 = <StatusIcon status={suite.status} size={12} className="mr-0.5" />;
    $[7] = suite.status;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = suite.name || "Ungrouped Tests";
  let t6;
  if ($[9] !== t5) {
    t6 = <HoverTickerText text={t5} className="ml-0.5 type-body-s-regular flex-1 min-w-0 text-neutral-900 dark:text-white" />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== showDuration || $[12] !== totalDuration) {
    t7 = showDuration && totalDuration > 0 && <span className="text-[12px] text-neutral-400 dark:text-neutral-500 tabular-nums flex-shrink-0 mr-1">{formatTestDuration(totalDuration)}</span>;
    $[11] = showDuration;
    $[12] = totalDuration;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== handleClick || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t6 || $[19] !== t7) {
    t8 = <button onClick={handleClick} className={t2}>{t3}{t4}{t6}{t7}</button>;
    $[14] = handleClick;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== handlePointerDown || $[22] !== t8) {
    t9 = <div className="select-text" onPointerDown={handlePointerDown}>{t8}</div>;
    $[21] = handlePointerDown;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
},
// Custom comparison: only re-render if expansion, hover, showDuration, or suite data changed
(prevProps, nextProps) => {
  return prevProps.isExpanded === nextProps.isExpanded && prevProps.isHovered === nextProps.isHovered && prevProps.showDuration === nextProps.showDuration && prevProps.suite.name === nextProps.suite.name && prevProps.suite.status === nextProps.suite.status && prevProps.suite.tests === nextProps.suite.tests;
});
export const TestList = React.memo(function TestList({
  checks,
  isLoading,
  onTestSelect,
  selectedTestId: externalSelectedTestId,
  isActive = true,
  repository,
  org,
  runId,
  jobId,
  branch,
  prNumber,
  onNavigationStart,
  logs,
  steps,
  isLogsLoading,
  // Filter state from parent (single source of truth)
  statusFilters,
  timeRangeHours,
  branchFilter,
  zoomStart,
  zoomEnd,
  onStatusFiltersChange,
  onTimeRangeChange,
  onBranchFilterChange,
  onZoomChange,
  isPendingDefaultFilter,
  // Job filter (workflow-level only)
  jobFilters = EMPTY_JOB_FILTERS,
  onJobFiltersChange
}: TestListProps) {
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const api = useApi();
  const {
    codesmithEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300);
  const [expandedSuites, setExpandedSuites] = useState<Set<string>>(new Set());
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const [internalSelectedTestId, setInternalSelectedTestId] = useState<string | null>(null);
  // Keyboard navigation hover state - separate from selection
  // -1 means no hover state (will initialize from selection on first navigation)
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number>(-1);
  // Track hovered item identity to restore position after flatRows changes (e.g., suite toggle)
  // Format: { type: "suite", name: string } | { type: "test", id: string } | null
  const hoveredItemIdentityRef = useRef<{
    type: "suite";
    name: string;
  } | {
    type: "test";
    id: string;
  } | null>(null);
  const [selectedFailureRun, setSelectedFailureRun] = useState<TestHistoryRun | null>(null);
  const [isFailureDrawerOpen, setIsFailureDrawerOpen] = useState(false);
  // Initialize showCardErrors from URL or localStorage, default to false
  const [showCardErrors, setShowCardErrors] = useLocalStorage("show_card_errors", false);

  // Sorting state for test list
  const [sortField, setSortField] = useLocalStorage<SortField>("testListSortField", "match-logs");
  const [titleSortOrder, setTitleSortOrder] = useLocalStorage<TitleSortOrder>("testListTitleSortOrder", "alphabetical");
  const [durationSortOrder, setDurationSortOrder] = useLocalStorage<DurationSortOrder>("testListDurationSortOrder", "longest");

  // Sync showCardErrors with URL param on mount
  useEffect(() => {
    const urlValue = searchParams.get("show_card_errors");
    if (urlValue === "true") {
      setShowCardErrors(true);
    } else if (urlValue === "false") {
      setShowCardErrors(false);
    }
  }, [searchParams, setShowCardErrors]);
  const {
    user
  } = useAuth();

  // Use external selection if provided, otherwise use internal state
  const selectedTestId = externalSelectedTestId ?? internalSelectedTestId;

  // Track whether external control is active (for stable callbacks)
  const isExternallyControlledRef = useRef(externalSelectedTestId !== undefined);
  isExternallyControlledRef.current = externalSelectedTestId !== undefined;

  // Defer selection for heavy right-pane work so rapid navigation stays responsive
  const deferredSelectedTestId = useDeferredValue(selectedTestId);

  // Resizable sidebar state (this is the test list sidebar, not the main app sidebar)
  // useLocalStorage handles SSR properly - it defers localStorage access until after hydration
  const [testListSidebarWidth, setTestListSidebarWidth] = useLocalStorage("testListSidebarWidth", 260);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Subscribe to main sidebar state for reliable width recalculation
  const {
    state: sidebarState
  } = useSidebar();
  // Detect mobile viewport (<768px) where sidebar is hidden via CSS
  const isMobile = useIsMobile();

  // Calculate container width: viewport width - main sidebar width - padding
  // Uses sidebarState as the single source of truth for sidebar width
  // This is more reliable than DOM queries, especially on initial page load
  // when CSS transitions haven't completed yet
  const getContainerWidth = useCallback(() => {
    if (typeof window === "undefined") return 0;

    // At <768px (md breakpoint), sidebar is hidden via CSS `hidden md:block`
    // so we treat its width as 0
    let mainSidebarWidth = 0;
    if (!isMobile) {
      // Use sidebar state to determine width - Expanded: 256px (16rem), Collapsed: 48px (3rem)
      mainSidebarWidth = sidebarState === "collapsed" ? 48 : 256;
    }

    // Horizontal margin: 24px left (ml-6) + 8px right (mr-2) = 32px total
    const horizontalMargin = 32;
    return Math.max(0, window.innerWidth - mainSidebarWidth - horizontalMargin);
  }, [sidebarState, isMobile]);

  // Handle resize drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);
  useEffect(() => {
    if (!isResizing) return;
    let rafId: number | null = null;
    let pendingWidth: number | null = null;
    const handleMouseMove = (e_0: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const newWidth = e_0.clientX - containerRect.left;
      // Ensure right pane has at least 300px (or 30% of container if smaller)
      // Max sidebar width is 70% of container
      const maxSidebarWidth = containerWidth * 0.7;
      // Clamp between 260px min and 70% max (narrower min = more space for detail pane)
      pendingWidth = Math.max(260, Math.min(maxSidebarWidth, newWidth));

      // Throttle state updates using requestAnimationFrame
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          if (pendingWidth !== null) {
            setTestListSidebarWidth(pendingWidth);
          }
          rafId = null;
        });
      }
    };
    const handleMouseUp = () => {
      // Apply final width immediately on mouseup
      if (pendingWidth !== null) {
        setTestListSidebarWidth(pendingWidth);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      setIsResizing(false);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isResizing, setTestListSidebarWidth]);

  // Constrain test list sidebar width when window or main sidebar resizes
  // Use useLayoutEffect for immediate calculation to prevent flickering
  useLayoutEffect(() => {
    const handleResize = () => {
      const width = getContainerWidth();
      if (width <= 0) return;

      // Max sidebar width is 70% of container
      const maxSidebarWidth_0 = width * 0.7;

      // If current sidebar width exceeds max (70%), reduce it
      if (testListSidebarWidth > maxSidebarWidth_0) {
        setTestListSidebarWidth(Math.max(260, maxSidebarWidth_0));
      }
    };

    // Run immediately on mount
    handleResize();
    window.addEventListener("resize", handleResize);

    // Also listen for sidebar toggle using ResizeObserver
    const sidebarElement = document.querySelector('[data-slot="sidebar-container"]');
    const observers: ResizeObserver[] = [];
    if (sidebarElement) {
      const sidebarObserver = new ResizeObserver(handleResize);
      sidebarObserver.observe(sidebarElement);
      observers.push(sidebarObserver);
    }

    // Also observe the wrapper element to catch any container size changes
    if (wrapperRef.current) {
      const wrapperObserver = new ResizeObserver(handleResize);
      wrapperObserver.observe(wrapperRef.current);
      observers.push(wrapperObserver);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
      observers.forEach(observer => observer.disconnect());
    };
  }, [testListSidebarWidth, getContainerWidth, setTestListSidebarWidth]);

  // Recalculate sidebar constraint when main sidebar state changes (expanded/collapsed)
  // or when crossing the mobile breakpoint (sidebar hidden at <768px)
  useEffect(() => {
    const width_0 = getContainerWidth();
    if (width_0 <= 0) return;
    const maxSidebarWidth_1 = width_0 * 0.7;
    if (testListSidebarWidth > maxSidebarWidth_1) {
      setTestListSidebarWidth(Math.max(260, maxSidebarWidth_1));
    }
  }, [sidebarState, isMobile, getContainerWidth, testListSidebarWidth, setTestListSidebarWidth]);

  // Check if this is a workflow-level view (no jobId means workflow level)
  const isWorkflowLevel = !jobId;

  // Calculate available jobs from checks (for workflow-level job filter)
  const availableJobs = useMemo(() => {
    if (!isWorkflowLevel) return [];
    const jobCounts = new Map<string, number>();
    checks.forEach(test => {
      if (test.job_name) {
        jobCounts.set(test.job_name, (jobCounts.get(test.job_name) || 0) + 1);
      }
    });
    return Array.from(jobCounts.entries()).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [checks, isWorkflowLevel]);

  // Calculate suite metadata from ALL tests (unfiltered) - status should never change based on filters
  const suiteMetadata = useMemo(() => {
    const metadata = new Map<string, {
      status: "pass" | "fail" | "skip";
      passCount: number;
      failCount: number;
      skipCount: number;
    }>();

    // Group ALL tests by suite first
    const allSuiteTests = new Map<string, TestCheck[]>();
    checks.forEach(test_0 => {
      const suiteName = test_0.test_suite || "";
      if (!allSuiteTests.has(suiteName)) {
        allSuiteTests.set(suiteName, []);
      }
      allSuiteTests.get(suiteName)!.push(test_0);
    });

    // Calculate status from ALL tests in each suite
    allSuiteTests.forEach((tests, name_0) => {
      metadata.set(name_0, {
        status: calculateSuiteStatus(tests),
        passCount: tests.filter(t => t.test_status === "pass").length,
        failCount: tests.filter(t_0 => t_0.test_status === "fail" || t_0.test_status === "error").length,
        skipCount: tests.filter(t_1 => t_1.test_status === "skip").length
      });
    });
    return metadata;
  }, [checks]);

  // Group tests by suite and apply filters (but use pre-calculated status from suiteMetadata)
  const testSuites = useMemo(() => {
    const suiteMap = new Map<string, TestCheck[]>();

    // Filter tests based on search and status
    const filteredTests = checks.filter(test_1 => {
      // Status filter - use OR logic: show tests matching ANY selected status
      if (statusFilters.length > 0) {
        const matchesStatus = statusFilters.some(filter => {
          if (filter === "failed") {
            return test_1.test_status === "fail" || test_1.test_status === "error";
          }
          if (filter === "pass") {
            return test_1.test_status === "pass";
          }
          if (filter === "skipped") {
            return test_1.test_status === "skip";
          }
          return false;
        });
        if (!matchesStatus) {
          return false;
        }
      }

      // Job filter (workflow-level only) - use OR logic: show tests matching ANY selected job
      if (isWorkflowLevel && jobFilters.length > 0) {
        if (!test_1.job_name || !jobFilters.includes(test_1.job_name)) {
          return false;
        }
      }

      // Search filter
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const matchesName = test_1.test_name.toLowerCase().includes(query);
        const matchesSuite = test_1.test_suite?.toLowerCase().includes(query);
        return matchesName || matchesSuite;
      }
      return true;
    });

    // Group filtered tests by suite
    filteredTests.forEach(test_2 => {
      const suiteName_0 = test_2.test_suite || "";
      if (!suiteMap.has(suiteName_0)) {
        suiteMap.set(suiteName_0, []);
      }
      suiteMap.get(suiteName_0)!.push(test_2);
    });

    // Convert to array - use pre-calculated status from suiteMetadata (based on ALL tests)
    const suites: TestSuite[] = Array.from(suiteMap.entries()).map(([name_1, tests_0]) => {
      const meta = suiteMetadata.get(name_1) || {
        status: "pass" as const,
        passCount: 0,
        failCount: 0,
        skipCount: 0
      };
      return {
        name: name_1,
        tests: tests_0,
        // Only filtered tests for display
        status: meta.status,
        // Status from ALL tests
        passCount: meta.passCount,
        failCount: meta.failCount,
        skipCount: meta.skipCount
      };
    });

    // Sort suites based on sortField
    return suites.sort((a_0, b_0) => {
      // Default sorting: failed first, then by name
      if (sortField === "match-logs") {
        if (a_0.status === "fail" && b_0.status !== "fail") return -1;
        if (a_0.status !== "fail" && b_0.status === "fail") return 1;
        return a_0.name.localeCompare(b_0.name);
      }

      // Title sorting (alphabetical or reverse)
      if (sortField === "title") {
        const comparison = a_0.name.localeCompare(b_0.name);
        return titleSortOrder === "alphabetical" ? comparison : -comparison;
      }

      // Duration sorting - sort by total duration of tests in suite
      if (sortField === "duration") {
        const aDuration = a_0.tests.reduce((sum, t_2) => sum + (t_2.duration_seconds ?? 0), 0);
        const bDuration = b_0.tests.reduce((sum_0, t_3) => sum_0 + (t_3.duration_seconds ?? 0), 0);
        return durationSortOrder === "shortest" ? aDuration - bDuration : bDuration - aDuration;
      }
      return 0;
    });
  }, [checks, debouncedSearchQuery, statusFilters, suiteMetadata, isWorkflowLevel, jobFilters, sortField, titleSortOrder, durationSortOrder]);

  // Flat row model for virtualization: suite headers + expanded tests
  const flatRows = useMemo(() => {
    const rows: FlatRowType[] = [];
    testSuites.forEach(suite => {
      rows.push({
        type: "suite",
        suite
      });
      if (expandedSuites.has(suite.name)) {
        // Sort tests within suite based on sortField
        const sortedTests = [...suite.tests].sort((a_1, b_1) => {
          if (sortField === "match-logs") {
            const aFailed = a_1.test_status === "fail" || a_1.test_status === "error";
            const bFailed = b_1.test_status === "fail" || b_1.test_status === "error";
            if (aFailed !== bFailed) {
              return aFailed ? -1 : 1;
            }
            return 0;
          }
          if (sortField === "title") {
            const comparison_0 = a_1.test_name.localeCompare(b_1.test_name);
            return titleSortOrder === "alphabetical" ? comparison_0 : -comparison_0;
          }
          if (sortField === "duration") {
            const aDuration_0 = a_1.duration_seconds ?? 0;
            const bDuration_0 = b_1.duration_seconds ?? 0;
            return durationSortOrder === "shortest" ? aDuration_0 - bDuration_0 : bDuration_0 - aDuration_0;
          }
          return 0;
        });
        sortedTests.forEach(test_3 => {
          rows.push({
            type: "test",
            test: test_3,
            suiteName: suite.name
          });
        });
      }
    });
    return rows;
  }, [testSuites, expandedSuites, sortField, titleSortOrder, durationSortOrder]);

  // Build a map from testId -> row index for fast scroll-to-selection
  const testIdToRowIndex = useMemo(() => {
    const map = new Map<string, number>();
    flatRows.forEach((row, idx) => {
      if (row.type === "test") {
        map.set(row.test.id, idx);
      }
    });
    return map;
  }, [flatRows]);

  // Scroll container ref for virtualization
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Virtualizer for the left test tree
  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: index => {
      const row_0 = flatRows[index];
      return row_0?.type === "suite" ? ROW_HEIGHT_SUITE : ROW_HEIGHT_TEST;
    },
    overscan: 10
  });

  // Auto-expand all suites on initial load
  useEffect(() => {
    if (!hasAutoExpanded && testSuites.length > 0) {
      // Default suites to collapsed if they are passing; keep non-passing suites open
      // so failures/skips remain immediately visible.
      const defaultExpandedSuiteNames = testSuites.filter(s => s.status !== "pass").map(s_0 => s_0.name);
      setExpandedSuites(new Set(defaultExpandedSuiteNames));
      setHasAutoExpanded(true);
    }
  }, [testSuites, hasAutoExpanded]);

  // Auto-select the first test case after filters are determined:
  // - On initial load (if no external selection)
  // - After filters change (if no test is selected)
  useEffect(() => {
    // Tests panel is always mounted (hidden via opacity/pointer-events); avoid
    // auto-selecting while on other tabs to prevent filter state from being
    // mutated in the background.
    if (!isActive) {
      return;
    }

    // Skip if no tests are available, still loading, or filters are still being determined
    // isPendingDefaultFilter ensures we don't select from unfiltered data before default filter is applied
    if (testSuites.length === 0 || isLoading || isPendingDefaultFilter) {
      return;
    }

    // Skip if we already have a test selected (either from URL or internal)
    if (selectedTestId) {
      // Check if selected test is still visible after filtering
      const selectedTestVisible = testSuites.some(suite_0 => suite_0.tests.some(test_4 => test_4.id === selectedTestId));

      // If selected test is visible, we're done
      if (selectedTestVisible) {
        return;
      }

      // If selected test is filtered out and it's from URL, don't auto-select
      // (let the existing logic handle clearing filters)
      if (externalSelectedTestId) {
        return;
      }

      // If selected test is filtered out and it's internal, clear selection
      // so we can select a new first test
      setInternalSelectedTestId(null);
      onTestSelect?.(null);
    }

    // No test selected - select the first one
    const firstSuite = testSuites[0];
    if (firstSuite && firstSuite.tests.length > 0) {
      const firstTest = firstSuite.tests[0];
      setInternalSelectedTestId(firstTest.id);
      onTestSelect?.(firstTest);

      // Make sure the suite is expanded
      setExpandedSuites(prev => {
        const next = new Set(prev);
        next.add(firstSuite.name);
        return next;
      });
    }
  }, [testSuites, externalSelectedTestId, selectedTestId, isLoading, onTestSelect, statusFilters,
  // Re-run when filters change
  isPendingDefaultFilter,
  // Wait for default filter to be applied
  isActive]);

  // Find the selected test - use selectedTestId directly (not deferred) for immediate UI feedback
  const selectedTest = useMemo(() => {
    if (!selectedTestId) return null;
    return checks.find(test_5 => test_5.id === selectedTestId) || null;
  }, [checks, selectedTestId]);

  // Track previous selectedTestId to detect actual selection changes
  const prevSelectedTestIdRef = useRef<string | null>(null);

  // Refs for scroll-to-selection logic to avoid unnecessary effect re-runs
  const testIdToRowIndexRef = useRef(testIdToRowIndex);
  testIdToRowIndexRef.current = testIdToRowIndex;
  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;

  // Scroll to selected test ONLY when the selection actually changes
  // Using refs for testIdToRowIndex and virtualizer to avoid re-running when they change
  useEffect(() => {
    if (!selectedTestId || isLoading) return;

    // Only scroll if the selected test actually changed
    if (prevSelectedTestIdRef.current === selectedTestId) {
      return;
    }
    prevSelectedTestIdRef.current = selectedTestId;
    const rowIndex = testIdToRowIndexRef.current.get(selectedTestId);
    if (rowIndex === undefined) return;

    // Check if item is already visible - if so, don't scroll
    const range = virtualizerRef.current.range;
    if (range && rowIndex >= range.startIndex && rowIndex <= range.endIndex) {
      // Item is already visible, no need to scroll
      return;
    }

    // Item is outside visible range, scroll to bring it into view
    virtualizerRef.current.scrollToIndex(rowIndex, {
      align: "auto"
    });
  }, [selectedTestId, isLoading]);

  // Get branch from checks (all checks should have the same branch for a job)
  const branchFromChecks = useMemo(() => {
    if (checks.length === 0) return null;
    // Get branch from first check that has a branch
    const checkWithBranch = checks.find(check => check.branch);
    return checkWithBranch?.branch ?? null;
  }, [checks]);

  // Get current branch for filtering (from checks, not props)
  // Also needed for "all" mode to show triangles for current branch runs
  const currentBranch = useMemo(() => {
    if (branchFilter !== "current" && branchFilter !== "main_and_current" && branchFilter !== "all") {
      return undefined;
    }
    // Use branch from checks, fallback to props for backward compatibility
    const branchToUse = branchFromChecks ?? branch ?? null;
    // Preserve null (which means branch is not available) vs undefined (which means not needed)
    return branchToUse ? branchToUse : undefined;
  }, [branchFilter, branchFromChecks, branch]);

  // Log when branch is required but not available
  useEffect(() => {
    if ((branchFilter === "current" || branchFilter === "main_and_current") && selectedTest && (!currentBranch || typeof currentBranch !== "string" || currentBranch.trim() === "")) {
      console.warn(`[TestList] Cannot fetch test history: branchFilter is "${branchFilter}" but branch is not available`, {
        branchFilter,
        branchFromChecks,
        branch,
        currentBranch,
        selectedTest: {
          id: selectedTest.id,
          name: selectedTest.test_name,
          suite: selectedTest.test_suite
        }
      });
    }
  }, [branchFilter, currentBranch, branchFromChecks, branch, selectedTest]);

  // Fetch actors for avatar lookups
  const {
    data: actorMap
  } = useActors(org);

  // Calculate max duration per suite for progress bar scaling
  const maxDurationPerSuite = useMemo(() => {
    const maxDurations = new Map<string, number>();
    testSuites.forEach(suite_1 => {
      const durations = suite_1.tests.map(test_6 => test_6.duration_seconds ?? 0).filter(d => d > 0);
      if (durations.length > 0) {
        maxDurations.set(suite_1.name, Math.max(...durations));
      } else {
        maxDurations.set(suite_1.name, 0);
      }
    });
    return maxDurations;
  }, [testSuites]);
  const toggleSuite = useCallback((suiteName_1: string) => {
    setExpandedSuites(prev_0 => {
      const next_0 = new Set(prev_0);
      if (next_0.has(suiteName_1)) {
        next_0.delete(suiteName_1);
      } else {
        next_0.add(suiteName_1);
      }
      return next_0;
    });
  }, []);

  // Memoized callback for run clicks to prevent TestHistoryPanel re-renders
  const handleRunClick = useCallback((run: TestHistoryRun) => {
    setSelectedFailureRun(run);
    setIsFailureDrawerOpen(true);
  }, []);

  // Memoized callback for failure drawer state changes
  const handleFailureDrawerOpenChange = useCallback((open: boolean) => {
    setIsFailureDrawerOpen(open);
    if (!open) {
      setSelectedFailureRun(null);
    }
  }, []);

  // Prefetch test history data when hovering over a test
  const prefetchTestHistory = useCallback((test_7: TestCheck) => {
    if (!org || !repository || !test_7.test_name) return;

    // Skip prefetch if branch filter requires currentBranch but it's not available
    if ((branchFilter === "current" || branchFilter === "main_and_current") && (!currentBranch || typeof currentBranch !== "string" || currentBranch.trim() === "")) {
      return;
    }

    // Calculate date range based on current timeRangeHours
    const endDate = new Date();
    const startDate = new Date();
    startDate.setHours(startDate.getHours() - timeRangeHours);
    const useDateTimePrecision = timeRangeHours < 24;
    const queryKey = ["testHistory", org, repository, test_7.test_suite ?? null, test_7.test_name, test_7.test_type === "junit" ? "junit" : "auto", timeRangeHours, branchFilter, currentBranch];
    queryClient.prefetchQuery(queryKey, async () => {
      const params: Record<string, string | number | null> = {
        test_suite: test_7.test_suite ?? null,
        test_name: test_7.test_name,
        test_type: test_7.test_type === "junit" ? "junit" : "auto",
        start_date: useDateTimePrecision ? startDate.toISOString() : startDate.toISOString().split("T")[0],
        end_date: useDateTimePrecision ? endDate.toISOString() : endDate.toISOString().split("T")[0],
        branch_filter: branchFilter,
        repository: repository,
        limit: 1000
      };
      if ((branchFilter === "current" || branchFilter === "main_and_current") && currentBranch) {
        params.current_branch = currentBranch;
      }
      const response = await api.get(`user/github/orgs/${org}/metrics/actions/tests/history`, {
        params
      });
      return response.data;
    }, {
      staleTime: 30000 // Consider data fresh for 30 seconds
    });
  }, [org, repository, timeRangeHours, branchFilter, currentBranch, queryClient, api]);

  // Wrapper for prefetch that handles null (for TestRow onHover compatibility)
  const handleTestHover = useCallback((test_8: TestCheck | null) => {
    if (test_8) {
      prefetchTestHistory(test_8);
    }
  }, [prefetchTestHistory]);

  // Ref for stable hover callback
  const handleTestHoverRef = useRef(handleTestHover);
  useEffect(() => {
    handleTestHoverRef.current = handleTestHover;
  }, [handleTestHover]);

  // Stable callback wrapper for hover
  const stableHandleTestHover = useCallback((test_9: TestCheck | null) => {
    handleTestHoverRef.current(test_9);
  }, [] // No dependencies - always uses ref
  );

  // Ref to track when we're intentionally clearing filters to prevent feedback loops
  const isClearingFiltersRef = useRef(false);

  // Tracks which external test id we already auto-expanded a suite for, so the
  // effect below only forces the suite open once per deep-linked test. Without
  // this guard, any re-run of the effect (e.g. from dep identity churn) would
  // instantly undo a user's collapse of that suite.
  const autoExpandedForTestIdRef = useRef<string | null>(null);

  // Ref for onStatusFiltersChange to avoid infinite loop when callback reference changes
  const onStatusFiltersChangeRef = useRef(onStatusFiltersChange);
  onStatusFiltersChangeRef.current = onStatusFiltersChange;

  // Handle URL-based test selection - clear filters if needed
  useEffect(() => {
    // Only perform visibility/clearing logic while actively on the Tests tab.
    // Otherwise we can clear the default "failed" filter even before the user
    // opens the Tests tab.
    if (!isActive) return;

    // Only run when we have an external test ID and data is loaded
    if (!externalSelectedTestId) {
      // Selection was cleared; a future deep link to the same test should
      // auto-expand its suite again
      autoExpandedForTestIdRef.current = null;
      return;
    }
    if (isLoading || checks.length === 0) return;

    // Skip if we're currently clearing filters to prevent feedback loop
    if (isClearingFiltersRef.current) return;

    // Check if the test exists in the full (unfiltered) checks
    const testExistsInChecks = checks.find(test_10 => test_10.id === externalSelectedTestId);
    if (!testExistsInChecks) return; // Test doesn't exist at all

    // Check if test is visible in filtered testSuites
    const testVisibleInFiltered = testSuites.some(suite_2 => suite_2.tests.some(test_11 => test_11.id === externalSelectedTestId));

    // If test exists but is filtered out due to status filters, clear the status filters so the
    // deep-linked test is visible.
    //
    // Important: do NOT clear the user's search input. If the user searches such that the
    // selected test is no longer visible, that's expected behavior.
    //
    // Also important: do NOT clear status filters if the test is hidden by job filters.
    // Check if the test would be visible with current job filters (ignoring status filters).
    // A test is hidden by job filters if:
    // 1. Job filters are active, AND
    // 2. The test has no job_name OR its job_name is not in the selected filters
    // This matches the actual filtering logic at line ~1301
    const hiddenByJobFilter = isWorkflowLevel && jobFilters.length > 0 && (!testExistsInChecks.job_name || !jobFilters.includes(testExistsInChecks.job_name));
    if (!testVisibleInFiltered && statusFilters.length > 0 && !debouncedSearchQuery && !hiddenByJobFilter) {
      isClearingFiltersRef.current = true;
      onStatusFiltersChangeRef.current([]);
      // Reset the ref after the debounce window passes
      setTimeout(() => {
        isClearingFiltersRef.current = false;
      }, 350);
    }

    // Ensure the test's suite is expanded, but only once per external test id
    // so the user can still collapse the suite afterwards. Wait for the
    // initial default expansion: it replaces expandedSuites wholesale, so
    // expanding earlier would be wiped while the once-only ref blocks a retry.
    if (hasAutoExpanded && autoExpandedForTestIdRef.current !== externalSelectedTestId) {
      autoExpandedForTestIdRef.current = externalSelectedTestId;
      const suite_3 = testExistsInChecks.test_suite || "";
      setExpandedSuites(prev_1 => {
        // Only create a new Set if the suite isn't already expanded
        // This prevents unnecessary re-renders
        if (prev_1.has(suite_3)) {
          return prev_1;
        }
        const next_1 = new Set(prev_1);
        next_1.add(suite_3);
        return next_1;
      });
    }
  }, [externalSelectedTestId, isLoading, checks, testSuites, statusFilters.length, debouncedSearchQuery, isActive, isWorkflowLevel, jobFilters, hasAutoExpanded]);
  const clearStatusFilters = useCallback(() => {
    isClearingFiltersRef.current = true;
    onStatusFiltersChange([]);
    // Reset after a short delay
    setTimeout(() => {
      isClearingFiltersRef.current = false;
    }, 100);
  }, [onStatusFiltersChange]);
  const clearJobFilters = useCallback(() => {
    isClearingFiltersRef.current = true;
    onJobFiltersChange?.([]);
    // Reset after a short delay
    setTimeout(() => {
      isClearingFiltersRef.current = false;
    }, 100);
  }, [onJobFiltersChange]);

  // Handle test selection - update internal state and call external handler
  // Uses ref for external control check to keep callback stable
  const handleTestSelect = useCallback((test_12: TestCheck | null) => {
    const newTestId = test_12?.id ?? null;
    // Only update internal state if external isn't controlling selection
    if (!isExternallyControlledRef.current) {
      setInternalSelectedTestId(newTestId);
    }
    onTestSelect?.(test_12);
  }, [onTestSelect]);

  // Refs for keyboard navigation to avoid stale closures and dropped rapid key presses
  const selectedTestIdRef = useRef<string | null>(selectedTestId);
  useEffect(() => {
    selectedTestIdRef.current = selectedTestId;
  }, [selectedTestId]);
  const handleTestSelectRef = useRef(handleTestSelect);
  useEffect(() => {
    handleTestSelectRef.current = handleTestSelect;
  }, [handleTestSelect]);

  // Refs for flatRows and toggleSuite to avoid stale closures in keyboard handler
  const flatRowsRef = useRef<FlatRowType[]>(flatRows);
  useEffect(() => {
    flatRowsRef.current = flatRows;
  }, [flatRows]);
  const toggleSuiteRef = useRef(toggleSuite);
  useEffect(() => {
    toggleSuiteRef.current = toggleSuite;
  }, [toggleSuite]);

  // Ref for hoveredItemIndex to avoid stale closures
  const hoveredItemIndexRef = useRef<number>(hoveredItemIndex);
  useEffect(() => {
    hoveredItemIndexRef.current = hoveredItemIndex;
  }, [hoveredItemIndex]);

  // Ref for isActive to check if test list tab is active
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Stable callback wrapper that always uses the latest handleTestSelect
  // This prevents stale closures in memoized TestRow components
  const stableHandleTestSelect = useCallback((test_13: TestCheck | null) => {
    handleTestSelectRef.current(test_13);
  }, [] // No dependencies - always uses ref
  );

  // Shared helper to clear hover state
  // Used by mouse move handler and flatRows restore effect
  const clearHoverState = useCallback(() => {
    setHoveredItemIndex(-1);
    hoveredItemIndexRef.current = -1;
    hoveredItemIdentityRef.current = null;
  }, [setHoveredItemIndex]);

  // Shared helper to update hover state and track identity
  // Used by both keyboard handler and search input arrow key navigation
  const updateHoverState = useCallback((index_0: number, rows_0: FlatRowType[]) => {
    hoveredItemIndexRef.current = index_0;
    setHoveredItemIndex(index_0);
    const row_1 = rows_0[index_0];
    if (row_1.type === "suite") {
      hoveredItemIdentityRef.current = {
        type: "suite",
        name: row_1.suite.name
      };
    } else {
      hoveredItemIdentityRef.current = {
        type: "test",
        id: row_1.test.id
      };
    }
  }, [setHoveredItemIndex]);

  // Ref for updateHoverState to avoid stale closures in keyboard handler
  const updateHoverStateRef = useRef(updateHoverState);
  useEffect(() => {
    updateHoverStateRef.current = updateHoverState;
  }, [updateHoverState]);

  // Keyboard navigation for test cases and suites
  useEffect(() => {
    const handleKeyDown = (e_1: KeyboardEvent) => {
      // Only handle keys when test list tab is active
      if (!isActiveRef.current) {
        return;
      }

      // Only handle keys if not typing in an input/textarea
      if (e_1.target instanceof HTMLInputElement || e_1.target instanceof HTMLTextAreaElement || e_1.target instanceof HTMLElement && e_1.target.isContentEditable) {
        return;
      }

      // Allow default browser scroll behavior with cmd/ctrl modifiers
      if (e_1.metaKey || e_1.ctrlKey) {
        return;
      }

      // Handle '/' to focus search input (works even when no tests visible)
      if (e_1.key === "/") {
        e_1.preventDefault();
        setIsSearchExpanded(true);
        // Also focus directly in case search is already expanded (state won't change, so effect won't run)
        // Use setTimeout to ensure the input is rendered if it wasn't before
        setTimeout(() => searchInputRef.current?.focus(), 0);
        return;
      }

      // Handle 'f' to open filter dropdown (works even when no tests visible)
      if (e_1.key === "f") {
        e_1.preventDefault();
        setIsFilterDropdownOpen(true);
        setIsSortDropdownOpen(false);
        return;
      }
      const rows_1 = flatRowsRef.current;
      if (rows_1.length === 0) return;

      // Handle navigation keys (j/k and arrow keys)
      if (e_1.key === "ArrowDown" || e_1.key === "ArrowUp" || e_1.key === "j" || e_1.key === "k") {
        e_1.preventDefault();
        const currentIndex = hoveredItemIndexRef.current;
        const isMovingDown = e_1.key === "ArrowDown" || e_1.key === "j";

        // If no hover state, try to start from currently selected test
        if (currentIndex === -1) {
          const currentId = selectedTestIdRef.current;
          if (currentId) {
            const selectedIndex = rows_1.findIndex(row_2 => row_2.type === "test" && row_2.test.id === currentId);
            if (selectedIndex !== -1) {
              // Start at selected item (don't move yet on first keypress)
              updateHoverStateRef.current(selectedIndex, rows_1);
              return;
            }
          }
        }

        // Calculate next index with wrap-around
        const newIndex = calculateNextIndex(currentIndex, rows_1.length, isMovingDown);
        updateHoverStateRef.current(newIndex, rows_1);
        return;
      }

      // Handle action keys (Enter and ArrowLeft)
      if (e_1.key === "Enter" || e_1.key === "ArrowLeft") {
        const currentIndex_0 = hoveredItemIndexRef.current;
        if (currentIndex_0 === -1 || currentIndex_0 >= rows_1.length) return;
        e_1.preventDefault();
        const hoveredRow = rows_1[currentIndex_0];
        if (hoveredRow.type === "suite") {
          // Toggle suite expand/collapse
          toggleSuiteRef.current(hoveredRow.suite.name);
        } else if (hoveredRow.type === "test") {
          // Select the test
          selectedTestIdRef.current = hoveredRow.test.id;
          handleTestSelectRef.current(hoveredRow.test);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Auto-scroll to hovered item when it changes
  useEffect(() => {
    if (hoveredItemIndex >= 0 && hoveredItemIndex < flatRows.length) {
      // Check if item is already visible - if so, don't scroll
      const range_0 = virtualizer.range;
      if (range_0 && hoveredItemIndex >= range_0.startIndex && hoveredItemIndex <= range_0.endIndex) {
        // Item is already visible, no need to scroll
        return;
      }
      virtualizer.scrollToIndex(hoveredItemIndex, {
        align: "auto"
      });
    }
  }, [hoveredItemIndex, flatRows.length, virtualizer]);

  // Restore hover position when flatRows changes (e.g., suite toggled)
  // Try to find the same item by identity; if not found, reset to -1
  useEffect(() => {
    const identity = hoveredItemIdentityRef.current;
    if (!identity) {
      // No tracked identity, reset hover (but don't clear identity since it's already null)
      setHoveredItemIndex(-1);
      hoveredItemIndexRef.current = -1;
      return;
    }

    // Search for the item by identity in the new flatRows
    const newIndex_0 = flatRows.findIndex(row_3 => {
      if (identity.type === "suite" && row_3.type === "suite") {
        return row_3.suite.name === identity.name;
      }
      if (identity.type === "test" && row_3.type === "test") {
        return row_3.test.id === identity.id;
      }
      return false;
    });
    if (newIndex_0 !== -1) {
      // Item still exists, update index to new position
      setHoveredItemIndex(newIndex_0);
      hoveredItemIndexRef.current = newIndex_0;
    } else {
      // Item no longer exists (e.g., test inside collapsed suite), reset
      clearHoverState();
    }
  }, [flatRows, clearHoverState]);

  // Fix AI button state
  const [selectedAction, setSelectedAction] = useLocalStorage<"fix-cursor" | "fix-codesmith" | "copy-prompt">("fix-ai-button-action", "fix-codesmith");
  const [copied, setCopied] = useState(false);

  // When codesmith is enabled, migrate existing "fix-cursor" users to "fix-codesmith" once
  const codesmithMigratedRef = useRef(false);
  useEffect(() => {
    if (codesmithEnabled && selectedAction === "fix-cursor" && !codesmithMigratedRef.current) {
      const migrated = localStorage.getItem("fix-ai-codesmith-migrated");
      if (!migrated) {
        setSelectedAction("fix-codesmith");
        localStorage.setItem("fix-ai-codesmith-migrated", "true");
      }
      codesmithMigratedRef.current = true;
    }
  }, [codesmithEnabled, selectedAction, setSelectedAction]);

  // Runtime fallback: if stored action is "fix-codesmith" but org doesn't have it, fall back
  const effectiveAction = useMemo(() => {
    if (selectedAction === "fix-codesmith" && !codesmithEnabled) return "fix-cursor";
    return selectedAction;
  }, [selectedAction, codesmithEnabled]);
  const isSelectedTestFailing = useMemo(() => {
    if (!selectedTest) return false;
    return selectedTest.test_status === "fail" || selectedTest.test_status === "error";
  }, [selectedTest]);

  // Get visible failed tests count
  const visibleFailedTestsCount = useMemo(() => {
    return testSuites.flatMap(suite_4 => suite_4.tests).filter(t_4 => t_4.test_status === "fail" || t_4.test_status === "error").length;
  }, [testSuites]);
  const buildFixPrompt = useCallback((tests_1: TestCheck[], includeLogs: boolean = true) => {
    let prompt = tests_1.length > 1 ? "Fix these failing tests:\n\n" : "Fix this failing test:\n\n";

    // Include context: repository, branch, PR, and run link
    prompt += `Repository: ${repository}\n`;
    if (branch) {
      prompt += `Branch: ${branch}\n`;
    }
    if (prNumber) {
      prompt += `PR: https://github.com/${repository}/pull/${prNumber}\n`;
    }
    prompt += `Run: https://github.com/${repository}/actions/runs/${runId}\n\n`;
    tests_1.forEach((test_14, index_1) => {
      const displayText = test_14.test_suite ? `${test_14.test_suite}/${test_14.test_name}` : test_14.test_name;
      prompt += `${index_1 + 1}. ${displayText}\n`;
    });

    // Include error logs if fewer than 10 tests and includeLogs is true
    if (includeLogs && tests_1.length < 10) {
      prompt += "\n---\nError logs:\n";
      tests_1.forEach(test_15 => {
        if (test_15.logs) {
          const displayText_0 = test_15.test_suite ? `${test_15.test_suite}/${test_15.test_name}` : test_15.test_name;
          prompt += `\n### ${displayText_0}\n${test_15.logs}\n`;
        }
      });
    }
    return prompt;
  }, [repository, branch, prNumber, runId]);

  // Generate fix prompt from visible failed tests
  // includeLogs: when false, excludes error logs to keep URLs short (for Cursor)
  const generateFixPrompt = useCallback((includeLogs_0: boolean = true) => {
    // Get failed tests from currently filtered/visible testSuites
    const visibleFailedTests = testSuites.flatMap(suite_5 => suite_5.tests).filter(t_5 => t_5.test_status === "fail" || t_5.test_status === "error");
    return buildFixPrompt(visibleFailedTests, includeLogs_0);
  }, [buildFixPrompt, testSuites]);

  // Generate fix prompt for the selected test only
  const generateSingleTestPrompt = useCallback((includeLogs_1: boolean = true) => {
    if (!selectedTest || !isSelectedTestFailing) return "";
    return buildFixPrompt([selectedTest], includeLogs_1);
  }, [buildFixPrompt, selectedTest, isSelectedTestFailing]);

  // Handle Fix in Cursor action
  // Try with logs first, fall back to without logs if URL would be too long
  const handleFixInCursor = useCallback(() => {
    const MAX_URL_LENGTH = 2000; // Safe limit for most browsers
    const baseUrl = "cursor://anysphere.cursor-deeplink/prompt?text=";

    // First try with logs included
    const promptWithLogs = generateFixPrompt(true);
    const urlWithLogs = `${baseUrl}${encodeURIComponent(promptWithLogs)}`;

    // If URL is within limits, use it; otherwise fall back to without logs
    const finalUrl = urlWithLogs.length <= MAX_URL_LENGTH ? urlWithLogs : `${baseUrl}${encodeURIComponent(generateFixPrompt(false))}`;
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  }, [generateFixPrompt]);

  // Handle Fix in Cursor for selected test only
  const handleFixSingleTestInCursor = useCallback(() => {
    if (!selectedTest || !isSelectedTestFailing) return;
    const MAX_URL_LENGTH_0 = 2000; // Safe limit for most browsers
    const baseUrl_0 = "cursor://anysphere.cursor-deeplink/prompt?text=";

    // First try with logs included
    const promptWithLogs_0 = generateSingleTestPrompt(true);
    const urlWithLogs_0 = `${baseUrl_0}${encodeURIComponent(promptWithLogs_0)}`;

    // If URL is within limits, use it; otherwise fall back to without logs
    const finalUrl_0 = urlWithLogs_0.length <= MAX_URL_LENGTH_0 ? urlWithLogs_0 : `${baseUrl_0}${encodeURIComponent(generateSingleTestPrompt(false))}`;
    window.open(finalUrl_0, "_blank", "noopener,noreferrer");
  }, [generateSingleTestPrompt, selectedTest, isSelectedTestFailing]);

  // Build the navigation URL for a Codesmith fix session, forwarding the
  // originating PR (when known) so the new session is linked to the existing
  // managed PR instead of opening a fresh one.
  const buildCodesmithSessionUrl = useCallback((prompt_0: string) => {
    const params_0 = new URLSearchParams();
    params_0.set("prompt", prompt_0);
    if (prNumber && repository) {
      params_0.set("repo", repository);
      params_0.set("pr", String(prNumber));
    }
    return `/${org}/sessions/new?${params_0.toString()}`;
  }, [org, prNumber, repository]);

  // Handle Fix in Codesmith action - navigates to Codesmith with fix prompt
  const handleFixInCodesmith = useCallback(() => {
    const prompt_1 = generateFixPrompt(true);
    router.push(buildCodesmithSessionUrl(prompt_1));
  }, [generateFixPrompt, router, buildCodesmithSessionUrl]);

  // Handle Fix in Codesmith for selected test only
  const handleFixSingleTestInCodesmith = useCallback(() => {
    if (!selectedTest || !isSelectedTestFailing) return;
    const prompt_2 = generateSingleTestPrompt(true);
    router.push(buildCodesmithSessionUrl(prompt_2));
  }, [generateSingleTestPrompt, selectedTest, isSelectedTestFailing, router, buildCodesmithSessionUrl]);

  // Handle Copy Fix Prompt for selected test only
  const handleCopySingleTestPrompt = useCallback(async () => {
    if (!selectedTest || !isSelectedTestFailing) return;
    const prompt_3 = generateSingleTestPrompt();
    const ok = await copyToClipboard(prompt_3);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generateSingleTestPrompt, selectedTest, isSelectedTestFailing]);

  // Handle Copy Fix Prompt for all visible failed tests
  const handleCopyPrompt = useCallback(async () => {
    const prompt_4 = generateFixPrompt();
    const ok_0 = await copyToClipboard(prompt_4);
    if (!ok_0) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generateFixPrompt]);

  // Handle dropdown item selection (changes default and executes)
  const handleActionSelect = useCallback((action: "fix-cursor" | "fix-codesmith" | "copy-prompt") => {
    setSelectedAction(action);
  }, [setSelectedAction]);
  const handleFixAllAction = useCallback(() => {
    if (effectiveAction === "fix-codesmith") {
      handleFixInCodesmith();
      return;
    }
    if (effectiveAction === "fix-cursor") {
      handleFixInCursor();
      return;
    }
    handleCopyPrompt();
  }, [effectiveAction, handleFixInCodesmith, handleFixInCursor, handleCopyPrompt]);

  // Calculate totals
  const failedTests = checks.filter(t_6 => t_6.test_status === "fail" || t_6.test_status === "error").length;

  // Calculate status filter counts
  const statusFilterCounts = useMemo(() => {
    return {
      failed: checks.filter(t_7 => t_7.test_status === "fail" || t_7.test_status === "error").length,
      pass: checks.filter(t_8 => t_8.test_status === "pass").length,
      skipped: checks.filter(t_9 => t_9.test_status === "skip").length
    };
  }, [checks]);

  // State for search expansion
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // State for filter and sort dropdowns (mutual exclusion: only one open at a time)
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const handleFilterOpenChange = useCallback((open_0: boolean) => {
    setIsFilterDropdownOpen(open_0);
    if (open_0) setIsSortDropdownOpen(false);
  }, []);
  const handleSortOpenChange = useCallback((open_1: boolean) => {
    setIsSortDropdownOpen(open_1);
    if (open_1) setIsFilterDropdownOpen(false);
  }, []);

  // If there's text, the search UI should never be in a collapsed state.
  useEffect(() => {
    if (searchQuery && !isSearchExpanded) {
      setIsSearchExpanded(true);
    }
  }, [searchQuery, isSearchExpanded]);

  // Focus search input when expanded
  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  // Loading: show skeleton that matches Tests tab layout to avoid layout shift (DESIGN.md).
  if (isLoading || isPendingDefaultFilter) {
    return <TestListSkeleton />;
  }
  return <div className="flex flex-col flex-1 min-h-full overflow-x-hidden bg-app-background">
      {/* Floating pane with content - constrained to viewport height */}
      <div ref={wrapperRef} className="flex mt-6 mb-6 ml-6 mr-2 overflow-hidden" style={{
      width: "calc(100% - 48px)",
      height: "calc(100vh - 162px)"
    }}>
        <div ref={containerRef} className={cn("flex w-full bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] dark:border-[var(--color-border)] shadow-sm overflow-hidden min-h-0", isResizing && "select-none cursor-col-resize")}>
          {/* Left Sidebar - Test List (scrollable) */}
          <div className="flex flex-col min-h-0 flex-shrink-0" style={{
          width: `${testListSidebarWidth}px`,
          minWidth: "260px"
        }}>
            {/* Filter & Search Header */}
            <div className="flex items-center gap-3 h-[42px] px-4 border-b border-black/[0.08] dark:border-white/[0.08]">
              {/* Filter Icon with Dropdown */}
              <StatusFilterDropdown statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} counts={statusFilterCounts} controlledOpen={isFilterDropdownOpen} onControlledOpenChange={handleFilterOpenChange} jobFilters={isWorkflowLevel ? jobFilters : undefined} onJobFiltersChange={isWorkflowLevel ? onJobFiltersChange : undefined} availableJobs={isWorkflowLevel ? availableJobs : undefined} trigger={<button className="flex items-center justify-center w-[26px] h-[26px] rounded-lg cursor-pointer transition-colors text-[#868686] hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 outline-none focus:outline-none focus-visible:outline-none">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                  </button>} />

              {/* Sort Dropdown */}
              <SortDropdown sortField={sortField} titleSortOrder={titleSortOrder} durationSortOrder={durationSortOrder} onSortFieldChange={setSortField} onTitleSortOrderChange={setTitleSortOrder} onDurationSortOrderChange={setDurationSortOrder} controlledOpen={isSortDropdownOpen} onControlledOpenChange={handleSortOpenChange} trigger={<button className="flex items-center justify-center w-[26px] h-[26px] rounded-lg cursor-pointer transition-colors text-[#868686] hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 outline-none focus:outline-none focus-visible:outline-none">
                    <ArrowDownUp className="w-4 h-4" />
                  </button>} />

              {/* Search Icon / Expandable Search */}
              <div className={cn(
            // Smooth expand/collapse:
            // - Animate max-width (flex changes like `flex-1` don't transition cleanly)
            // - Fade/slide the input instead of jumping between `w-0` and `flex-1`
            "flex items-center h-[26px] rounded-lg transition-[max-width,background-color,color] ease-out", isSearchExpanded || searchQuery ? "flex-1 max-w-full justify-between overflow-hidden border border-[#1A7EAD] dark:border-[#F0FB29] bg-neutral-100 dark:bg-neutral-800 pl-[4px] pr-1.5 py-px duration-300" : "min-w-[26px] max-w-[26px] justify-center border-0 duration-150")} onClick={() => {
              if (!isSearchExpanded && !searchQuery) {
                setIsSearchExpanded(true);
              }
            }}>
                {isSearchExpanded || searchQuery ? <>
                    <div className="flex items-center min-w-0 flex-1 gap-1">
                      {/* Inherit text color so hover states apply to the icon, too */}
                      <Search className="w-4 h-4 flex-shrink-0 text-current" />
                      <input ref={searchInputRef} type="text" placeholder="Search tests..." value={searchQuery} onChange={e_2 => setSearchQuery(e_2.target.value)} onKeyDown={e_3 => {
                    // Ensure Cmd/Ctrl+A selects all text even if global hotkeys exist.
                    if (e_3.key.toLowerCase() === "a" && (e_3.metaKey || e_3.ctrlKey)) {
                      e_3.preventDefault();
                      e_3.stopPropagation();
                      (e_3.currentTarget as HTMLInputElement).select();
                      return;
                    }

                    // Allow up/down arrow keys to navigate the test list while search is focused
                    if (e_3.key === "ArrowDown" || e_3.key === "ArrowUp") {
                      e_3.preventDefault();
                      e_3.stopPropagation();
                      const rows_2 = flatRows;
                      if (rows_2.length === 0) return;

                      // Use ref to avoid stale closure issues with rapid keypresses
                      const currentIndex_1 = hoveredItemIndexRef.current;
                      const isMovingDown_0 = e_3.key === "ArrowDown";

                      // If no hover state, try to start from currently selected test
                      if (currentIndex_1 === -1 && selectedTestId) {
                        const selectedIndex_0 = rows_2.findIndex(row_4 => row_4.type === "test" && row_4.test.id === selectedTestId);
                        if (selectedIndex_0 !== -1) {
                          // Start at selected item (don't move yet on first keypress)
                          updateHoverState(selectedIndex_0, rows_2);
                          return;
                        }
                      }

                      // Calculate next index with wrap-around
                      const newIndex_1 = calculateNextIndex(currentIndex_1, rows_2.length, isMovingDown_0);
                      updateHoverState(newIndex_1, rows_2);
                      return;
                    }

                    // Handle Enter to select the hovered item while search is focused
                    if (e_3.key === "Enter" && hoveredItemIndexRef.current !== -1) {
                      e_3.preventDefault();
                      e_3.stopPropagation();
                      const hoveredRow_0 = flatRows[hoveredItemIndexRef.current];
                      if (hoveredRow_0?.type === "suite") {
                        toggleSuite(hoveredRow_0.suite.name);
                      } else if (hoveredRow_0?.type === "test") {
                        handleTestSelect(hoveredRow_0.test);
                      }
                      return;
                    }
                    if (e_3.key !== "Escape") return;
                    if (searchQuery) return;
                    e_3.preventDefault();
                    e_3.stopPropagation();
                    setIsSearchExpanded(false);
                    (e_3.currentTarget as HTMLInputElement).blur();
                  }} onBlur={() => {
                    if (!searchQuery) {
                      setIsSearchExpanded(false);
                    }
                  }} className={cn("h-auto bg-transparent border-none p-0 text-sm text-neutral-900 dark:text-white placeholder:text-[#868686] outline-none focus:outline-none focus:ring-0 focus:border-none",
                  // Animate the text field in/out smoothly.
                  "transition-[max-width,opacity,transform] duration-200 ease-out", "flex-1 min-w-0 max-w-[1000px] opacity-100 translate-x-0")} style={{
                    boxShadow: "none"
                  }} />
                    </div>
                    <button onClick={e_4 => {
                  e_4.stopPropagation();
                  setSearchQuery("");
                  if (!searchQuery) {
                    setIsSearchExpanded(false);
                  } else {
                    searchInputRef.current?.focus();
                  }
                }} className="text-[#868686] hover:text-neutral-600 flex-shrink-0 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </> :
              // Collapsed: render a real button so the hover state matches the filter icon exactly.
              <button type="button" aria-label="Search tests" className="flex items-center justify-center w-[26px] h-[26px] rounded-lg transition-colors text-[#868686] hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 outline-none focus:outline-none focus-visible:outline-none">
                    <Search className="w-4 h-4 text-current" />
                  </button>}
              </div>
            </div>

            {/* Active Filter Badges - only shown when filters are active */}
            {(statusFilters.length > 0 || jobFilters.length > 0) && <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-black/[0.08] dark:border-white/[0.08]">
                {statusFilters.length > 0 && <StatusFilterDropdown statusFilters={statusFilters} onStatusFiltersChange={onStatusFiltersChange} openSubmenuDirectly={true} isClearingRef={isClearingFiltersRef} counts={statusFilterCounts} trigger={<FilterBadge interactive showCheckmark onClear={clearStatusFilters}>
                        <span>Status:</span>
                        <div className="flex items-center gap-2">
                          {statusFilters.map(filter_0 => <div key={filter_0} className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: getStatusColor(filter_0)
                  }} />
                              <span>{getStatusLabel(filter_0)}</span>
                            </div>)}
                        </div>
                      </FilterBadge>} />}
                {jobFilters.length > 0 && onJobFiltersChange && <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <JobFilterDropdown jobFilters={jobFilters} onJobFiltersChange={onJobFiltersChange} availableJobs={availableJobs} trigger={<FilterBadge interactive onClear={clearJobFilters}>
                                <Briefcase className="w-3 h-3" />
                                <span>Jobs:</span>
                                <span>
                                  {jobFilters.length} job
                                  {jobFilters.length !== 1 ? "s" : ""}
                                </span>
                              </FilterBadge>} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[300px]">
                        <div className="text-xs">
                          <div className="font-medium mb-1">Filtered jobs:</div>
                          <ul className="space-y-0.5">
                            {jobFilters.map(job => <li key={job} className="truncate">
                                {job}
                              </li>)}
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>}
              </div>}

            {/* Test Tree - Virtualized for performance with 2000+ tests */}
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-0.5" onMouseMove={() => {
            // Clear keyboard hover state when user moves mouse
            if (hoveredItemIndex !== -1) {
              clearHoverState();
            }
          }}>
              {flatRows.length === 0 ? <div className="p-4 text-center text-sm text-[#737373]">
                  {checks.length === 0 ? "No tests found" : "No matching tests"}
                </div> : <div style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative"
            }}>
                  {virtualizer.getVirtualItems().map(virtualRow => {
                const row_5 = flatRows[virtualRow.index];
                if (!row_5) return null;
                if (row_5.type === "suite") {
                  return <div key={`suite-${row_5.suite.name}`} style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}>
                          <SuiteRow suite={row_5.suite} isExpanded={expandedSuites.has(row_5.suite.name)} isHovered={hoveredItemIndex === virtualRow.index} showDuration={sortField === "duration"} onToggle={() => toggleSuite(row_5.suite.name)} />
                        </div>;
                }

                // Test row
                const isSelected = selectedTestId === row_5.test.id;
                const isHovered = hoveredItemIndex === virtualRow.index;
                return <div key={row_5.test.id} style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`
                }}>
                        <TestRow test={row_5.test} isSelected={isSelected} isHovered={isHovered} showDuration={sortField === "duration"} onTestSelect={stableHandleTestSelect} onHover={stableHandleTestHover} />
                      </div>;
              })}
                </div>}
            </div>
          </div>

          {/* Resizable Divider */}
          <div className="group relative flex-shrink-0 w-0">
            {/* Hit target - wider area for easy grabbing */}
            <div onMouseDown={handleMouseDown} className="absolute inset-y-0 -left-2 w-5 cursor-col-resize z-10" />
            {/* Visual line - 1px width, exactly at pane boundary */}
            <div className={cn("absolute inset-y-0 left-0 w-px transition-all pointer-events-none", "bg-neutral-200 dark:bg-neutral-800 group-hover:bg-neutral-300 dark:group-hover:bg-neutral-700", isResizing && "bg-blue-500 dark:bg-blue-500")} />
          </div>

          {/* Right Detail Pane - Recent Failures */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {!selectedTest ? <div className="flex-1 flex items-center justify-center">
                <div className="text-center" style={{
              color: COLORS.neutral
            }}>
                  <div className="text-sm">Select a test to view details</div>
                </div>
              </div> : selectedTest.test_type !== "junit" ?
          // Auto-parsed test: Use AutoParsedTestDetailView
          // Logs may still be loading - the component handles this internally on the Logs tab
          <AutoParsedTestDetailView test={selectedTest} logs={logs} steps={steps} isLogsLoading={isLogsLoading} org={org} repository={repository} runId={runId} jobId={jobId || ""} branch={branch} timeRangeHours={timeRangeHours} branchFilter={branchFilter} currentBranch={currentBranch} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} onTimeRangeChange={onTimeRangeChange} onBranchFilterChange={onBranchFilterChange} isResizing={isResizing} onNavigationStart={onNavigationStart} onRunClick={handleRunClick} showCardErrors={showCardErrors} actorMap={actorMap} isSuperuser={user?.is_superuser} /> :
          // JUnit test: Use JunitTestDetailView with Logs/History tabs
          <JunitTestDetailView test={selectedTest} logs={logs} steps={steps} isLogsLoading={isLogsLoading} org={org} repository={repository} runId={runId} jobId={jobId || ""} branch={branch} timeRangeHours={timeRangeHours} branchFilter={branchFilter} currentBranch={currentBranch} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} onTimeRangeChange={onTimeRangeChange} onBranchFilterChange={onBranchFilterChange} isResizing={isResizing} onNavigationStart={onNavigationStart} onRunClick={handleRunClick} showCardErrors={showCardErrors} actorMap={actorMap} isSuperuser={user?.is_superuser} maxDurationPerSuite={maxDurationPerSuite} />}
          </div>
        </div>
      </div>

      {/* Shared Test Failure Drawer */}
      <TestFailureDrawer run={selectedFailureRun} isOpen={isFailureDrawerOpen} onOpenChange={handleFailureDrawerOpenChange} org={org} repository={repository} testName={selectedTest?.test_name} jobId={jobId} />

      {/* Floating Fix AI Button - only shown when tab is active and there are failed tests */}
      {isActive && <FixAiFloatingButton visibleFailedTestsCount={visibleFailedTestsCount} selectedAction={effectiveAction} isPrimaryDisabled={!selectedTest || !isSelectedTestFailing} onFixAllTests={handleFixAllAction} onFixSingleTest={handleFixSingleTestInCursor} onFixSingleTestInCodesmith={handleFixSingleTestInCodesmith} onCopySingleTest={handleCopySingleTestPrompt} onSelectAction={handleActionSelect} copied={copied} codesmithEnabled={codesmithEnabled} />}
    </div>;
});
function _temp10(sum, test) {
  return sum + (test.duration_seconds ?? 0);
}